// WHAT THE RESEARCH PACKS HOLD — said once, at two levels (busy-my-web#437, #441).
//
// Saffet, in Slack, describing the two halves of one journey:
//
//   …just after saving into a pack          …and once the breadcrumb is walked back
//   Saved to                                2 Research Packs
//   Duck eggs?  [Research Pack] · Open ↗     8 Extractions
//    2 Extractions                           4 Photos
//    1 Product                               1 Product
//
// The two blocks are the SAME arithmetic at two depths: one pack's contents, and every pack's
// contents added up. So the counting lives here, in one pure module, and the pack briefing's own
// `countPack` (lib/briefings/pack.js) is what both are built from — a second tally would be a
// second answer to "how many photos", and the whole point of the aggregate is that it agrees with
// the pack rows underneath it.
//
// A plain value with no closures, like every other briefing part.

import { countPack } from './pack'
import { countLabel } from './rows'
import { storyPacks } from '@/lib/storySteps'
import { itemInPack } from '@/lib/packCompare'

/**
 * THE WORDS.
 *
 * `countPack`'s keys, in the reading order Saffet wrote them, each with its own glyph — the same
 * glyph the pack briefing's row for that kind already wears, so the aggregate and the pack rows
 * are one picture of one thing.
 *
 * EXTRACTIONS was, until 2026-09-15, the one word that was not the pack briefing's own ("2 snippets",
 * from frame 40008221:412348) — the reporter's word for the count. Pouria settled it that day: "we
 * don't have snippets any more, we changed it to Extractions, make sure everywhere is the same" — so
 * the pack briefing, the rail, the timeline steps and the pack document all say Extraction(s) now;
 * only the internal kind keys ('snippet'/'snippets') keep the old name.
 */
export const PACK_KINDS = [
  { key: 'snippets', icon: 'message-text', one: 'Extraction', many: 'Extractions' },
  { key: 'photos', icon: 'gallery', one: 'Photo', many: 'Photos' },
  // busy-my-web#546 (Pouria: "when we select videos it should calculate as videos not
  // extractions") — a saved video used to fall through `countPack`'s snippet fallback and read as
  // an Extraction. There is no dedicated video glyph in any icon pack yet (`src/assets/icon-packs`
  // — checked, none of the three carry one), so this reuses the Photos row's `gallery` glyph, the
  // closest existing token for saved media; the TEXT is what actually tells the two rows apart
  // ("N Photos" vs "N Videos"). Flagged for design/Figma to give it a real icon.
  // …and both in the pack briefing's own words since 2026-10-01 (Pouria: "we need to include
  // videos and links as well in briefing"): lib/briefings/pack.js's rows say "N video(s)" and
  // "N link(s)", and the Ongoing's Briefing called the same saved links "Products" — one thing, two
  // names, one level apart. `links` counts saved LINKS (`countPack`'s `of('link')`), so "Product"
  // was never even what it counted.
  { key: 'videos', icon: 'gallery', one: 'video', many: 'videos' },
  { key: 'links', icon: 'global', one: 'link', many: 'links' },
  { key: 'emails', icon: 'sms', one: 'Email', many: 'Emails' },
  { key: 'tasks', icon: 'tick-circle', one: 'Task', many: 'Tasks' },
  { key: 'events', icon: 'events', one: 'Event', many: 'Events' },
  { key: 'notes', icon: 'notes', one: 'Note', many: 'Notes' },
  { key: 'comparisons', icon: 'messages-3', one: 'Comparison', many: 'Comparisons' },
]

/** The kinds a pack holds that the STORY's own rows never count — see `aggregateRows`. */
// COMPARISONS joined the three on 2026-09-15 (parity audit E-7, busy-my-web#488). Frame
// 40008216:411421 draws the Ongoing-level Briefing as "3 research packs / 2 tasks / 1 comparison"
// the moment a comparison exists, and the story's own rows have no line for one: a comparison is
// a shared research item, not a step the timeline can be narrowed to, so #441's rule ("what the
// packs hold that is not a step on the story") puts it exactly here. The pack-level briefing has
// shown it since #153; this is the same count one level up.
// …and EXTRACTIONS and PHOTOS left it on 2026-09-15 (Pouria, local batch): "on the Ongoing Briefing we
// include the photos and extractions, which is wrong — they belong to the Research Pack, we have them
// in the middle." They are still counted one level down, on the pack's own briefing and the after-save
// strip (`packCountRows` with no `only`); the Ongoing-level Briefing names only what is not already
// on screen in the middle: the links (and, since 2026-10-01, the videos) and the comparisons.
// …and VIDEOS joined on 2026-10-01 (Pouria, same day as the pack-level rows: "we need to include
// videos and links as well in briefing"). This OVERRIDES busy-my-web#546's dev note, which kept
// videos out "same reasoning as photos — already shown in the middle": photos get their own delta
// chip on the pack node, but videos — like links — get none (lib/packDeltaChips.js `KINDS`) and are
// listed behind the pack's Links view beside the links. So the Ongoing names them beside the links,
// in the pack briefing's order.
// …and VIDEOS and LINKS left it again on 2026-10-16 (busy-my-web#643, Pouria: "remove the link and
// videos from the briefing"): the Ongoing Briefing's header no longer lists "N Videos" / "N Link(s)".
// The pack-level briefing (lib/briefings/pack.js) and PACK_KINDS keep counting them.
export const AGGREGATE_KINDS = ['comparisons']

/** "2 Research Packs" — the line the aggregate is headed with. */
export function packsLabel(count) {
  const n = Number(count) || 0
  return countLabel(n, 'Research Pack')
}

/**
 * The count rows for a `countPack`-shaped tally: `[{ key, icon, text, count }]`.
 *
 * ONLY THE NON-ZERO KINDS (Saffet's own answer to "which rows should appear"): a pack that holds
 * no photos says nothing about photos, exactly as every other briefing row behaves.
 *
 * @param counts a countPack result (or any subset of its keys)
 * @param only   restrict to these keys, in PACK_KINDS' order — the aggregate uses it to leave out
 *               the kinds the story briefing already counts for itself
 */
export function packCountRows(counts = {}, { only = null } = {}) {
  const keys = only ? new Set(only) : null
  return PACK_KINDS
    .filter(k => !keys || keys.has(k.key))
    .map((k) => {
      const n = Number(counts?.[k.key]) || 0
      return n ? { key: k.key, icon: k.icon, count: n, text: countLabel(n, k.one, k.many) } : null
    })
    .filter(Boolean)
}

/**
 * Every pack of this story, added up.
 *
 * Counted over the UNION of the packs' contents rather than pack by pack: an item ticked into two
 * packs is one thing the ongoing holds, and adding it twice would make the aggregate disagree with
 * the timeline it is describing. Membership is `itemInPack`'s rule (own + shared), asked of the
 * story's real packs — an item stamped with a pack that no longer exists is not in a pack.
 *
 * @param story the ongoing (its `research` items and `timeline` events)
 * @returns `{ packs, ...countPack keys }`
 */
export function aggregatePackCounts(story) {
  const packs = storyPacks(story)
  const ids = packs.map(p => p && p.id).filter(Boolean)
  const inAnyPack = (x) => ids.some(id => itemInPack(x, id))
  const items = (story?.research || []).filter(r => r && r.kind !== 'doc' && inAnyPack(r))
  const events = (story?.timeline || []).filter(e => e && inAnyPack(e))
  return { packs: packs.length, ...countPack(items, events) }
}

/**
 * The Ongoing-level aggregate's own rows (busy-my-web#441) — what the Briefing adds under its
 * "N research packs" row once the breadcrumb is walked back out to the root.
 *
 * ONLY the kinds in `AGGREGATE_KINDS`. The story briefing already counts its tasks, events,
 * notes and emails — pack-filed ones included, by its own recorded rule ("this number equals what
 * its own filter reveals", lib/briefings/story.js) — so repeating them here would say the same
 * number twice under two names. What it has never said is what the packs hold that is not a step
 * on the story. Saffet listed the Extractions, the photos and the products; Pouria took the first two
 * back out on 2026-09-15 — they belong to the pack and are already in the middle.
 *
 * Facts, not filters: a photo saved inside a pack is not a kind the story's timeline can be
 * narrowed to, and a row that looks clickable and does nothing is worse than a row that does not.
 */
export function aggregateRows(story) {
  const counts = aggregatePackCounts(story)
  if (!counts.packs) return []
  return packCountRows(counts, { only: AGGREGATE_KINDS })
    .map(r => ({ key: `pack-${r.key}`, icon: r.icon, text: r.text }))
}

export default aggregateRows
